import React, { FC, useEffect } from "react";

import { IPictoProps, Picto, useMediaQuery } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Link, Navigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useSubscription } from "@/hooks/useSubscription";

import { Avatar, FeatureGate } from "@/components/atoms";
import {
	bannerContext,
	bannerFor,
} from "@/components/organisms/SubscriptionBanner/SubscriptionBanner";
import { PageLayout } from "@/components/templates";

import { setPageTitle } from "@/utils/UPageTitle";

import { MOBILE_MEDIA_QUERY } from "@/constants/CConstants";
import { PLAN_NAMES } from "@/constants/CPlans";
import { UserRole } from "@/constants/CRoles";

/**
 * The mobile "Me" tab: every page the bottom bar has no room for, as
 * the desktop sidebar lists them, plus the account. Desktop has the
 * sidebar: it lands on the profile instead.
 */
export const Me: FC = () => {
	const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
	const { user, hasRole, logout } = useAuth();
	const { subscription, plan } = useSubscription();
	// Off-plan entries stay listed with a lock: the page explains.
	const pdf = usePlanGate("pdf");
	const invoicing = usePlanGate("invoices");

	useEffect(() => setPageTitle(t("me.title")), []);

	if (!isMobile) return <Navigate to="/profile" replace />;

	const banner = bannerFor(subscription, plan?.code ?? "free");
	const subscriptionHint = banner
		? t(`subscription.banner.${banner.kind}`, {
				count: banner.days,
				date: banner.date,
				plan: subscription ? PLAN_NAMES[subscription.plan] : "",
				context: bannerContext(banner.kind, user?.features),
			})
		: undefined;

	return (
		<PageLayout header={false} className="flex flex-col gap-6">
			<Link
				to="/profile"
				className="flex items-center gap-4 rounded-2xl bg-white p-4 text-neutral-900 no-underline! shadow-sm"
			>
				<Avatar url={user?.avatar} className="h-14! w-14! shrink-0" />
				<span className="flex min-w-0 grow flex-col">
					<span className="truncate text-lg font-semibold">
						{[user?.firstName, user?.lastName]
							.filter(Boolean)
							.join(" ")}
					</span>
					<span className="truncate text-sm text-neutral-500">
						{user?.mail}
					</span>
				</span>
				<Picto icon="chevron" className="h-4 w-4 text-neutral-400" />
			</Link>

			<MeSection title={t("me.sections.manage")}>
				<MeRow to="/views" picto="calendar" label={t("views.title")} />
				<MeRow
					to="/reports"
					picto="file"
					label={t("reports.title")}
					locked={pdf.locked}
				/>
				<FeatureGate feature="invoices">
					<MeRow
						to="/invoices"
						picto="fileText"
						label={t("invoices.title")}
						locked={invoicing.locked}
					/>
				</FeatureGate>
				<MeRow
					to="/companies"
					picto="briefcase"
					label={t("company.title")}
				/>
				<MeRow to="/clients" picto="target" label={t("client.title")} />
			</MeSection>

			<MeSection title={t("me.sections.account")}>
				<MeRow to="/profile" picto="user" label={t("account.title")} />
				<MeRow
					to="/subscription"
					picto="tag"
					label={t("subscription.title")}
					hint={subscriptionHint}
				/>
				<FeatureGate feature="sharing">
					<MeRow
						to="/workspace"
						picto="users"
						label={t("workspace.title")}
					/>
				</FeatureGate>
				{hasRole([UserRole.ADMIN]) && (
					<MeRow
						to="/admin/users"
						picto="settings"
						label={t("admin.title")}
					/>
				)}
				<button
					type="button"
					onClick={() => logout()}
					className="flex min-h-13 w-full items-center gap-4 px-4 py-2 text-left text-red-700 hover:bg-red-50"
				>
					<Picto icon="logout" className="h-5 w-5 shrink-0" />
					<span className="font-medium">{t("auth.logout")}</span>
				</button>
			</MeSection>
		</PageLayout>
	);
};

const MeSection: FC<{ title: string; children: React.ReactNode }> = ({
	title,
	children,
}) => (
	<section className="flex flex-col gap-2">
		<h2 className="px-2 text-xs font-semibold tracking-wider text-neutral-500 uppercase">
			{title}
		</h2>
		<div className="divide-y divide-neutral-100 overflow-hidden rounded-2xl bg-white shadow-sm">
			{children}
		</div>
	</section>
);

interface IMeRowProps {
	to: string;
	picto: IPictoProps["icon"];
	label: string;
	hint?: string;
	locked?: boolean;
}

const MeRow: FC<IMeRowProps> = ({ to, picto, label, hint, locked }) => (
	<Link
		to={to}
		className="flex min-h-13 items-center gap-4 px-4 py-2 text-neutral-900 no-underline! hover:bg-neutral-50"
	>
		<Picto icon={picto} className="text-primary-700 h-5 w-5 shrink-0" />
		<span className="flex min-w-0 grow flex-col">
			<span className="font-medium">{label}</span>
			{hint && (
				<span className="truncate text-xs text-neutral-500">
					{hint}
				</span>
			)}
		</span>
		{locked && (
			<span
				className="inline-flex shrink-0 items-center rounded-full bg-amber-50 px-2 py-0.5 text-amber-800"
				data-testid="me-lock"
			>
				<Picto icon="lock" className="h-3.5 w-3.5" />
			</span>
		)}
		<Picto icon="chevron" className="h-4 w-4 shrink-0 text-neutral-400" />
	</Link>
);
